import rawSankeyJson from "@/_data/dashboard/raw_sankey_data_092826.json";
import commitCreditorYearJson from "@/_data/dashboard/commit_creditor_year.json";
import commitmentCreditorTypesJson from "@/_data/dashboard/commitment_creditor_types.json";
import commitmentGtcJson from "@/_data/dashboard/commitment_GTC.json";
import commitmentGtcYearJson from "@/_data/dashboard/commitment_GTC_year.json";
import commitDenominationYearJson from "@/_data/dashboard/commit_demomination_year.json";
import commitmentGoverningLawJson from "@/_data/dashboard/commitment_governing_law.json";
import commitmentGoverningLawYearJson from "@/_data/dashboard/commitment_governing_law_year.json";
import commitInstrumentYearJson from "@/_data/dashboard/commitment_instrument_year.json";
import dfClausesJson from "@/_data/dashboard/df_clauses_092826.json";
import type { DocumentFlowRow, FlowRow, YearlyRow } from "./types";

type CreditorTypeTotalRow = {
  pCreditorType: string;
  "SUM(TotalCommitmentAmount)": number | null;
};

type GoverningLawTotalRow = {
  GoverningLaw: string | null;
  "SUM(TotalCommitmentAmount)": number | null;
};

type GtcTotalRow = {
  gtc: string | null;
  "SUM(TotalCommitmentAmount)": number | null;
};

type ClauseRow = {
  DOCID?: string;
  clause_group?: string | null;
  [key: string]: unknown;
};

type RawSankeyRow = {
  source: string;
  target: string;
  TotalCommitmentAmount: number | string | null;
  DocumentYear: number | string | null;
  complete_data?: boolean | string | null;
  CreditorGeographicRegion?: string | null;
  BorrowerGeographicRegion?: string | null;
  pCreditorType?: string | null;
  inferred_denominations_readable?: string | null;
};

const rawSankeyRows = rawSankeyJson.data as RawSankeyRow[];

const parseOptionalNumber = (value: number | string | null | undefined) => {
  if (value == null) return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseCompleteData = (value: boolean | string | null | undefined) => {
  if (typeof value === "boolean") return value;
  if (typeof value !== "string") return undefined;

  const normalized = value.trim().toLowerCase();
  if (normalized === "true") return true;
  if (normalized === "false") return false;

  return undefined;
};

export const flowRows: FlowRow[] = rawSankeyRows.map((row) => ({
  source: row.source,
  target: row.target,
  "SUM(TotalCommitmentAmount)": parseOptionalNumber(row.TotalCommitmentAmount),
  DocumentYear: parseOptionalNumber(row.DocumentYear),
  complete_data: parseCompleteData(row.complete_data),
  CreditorGeographicRegion: row.CreditorGeographicRegion,
  BorrowerGeographicRegion: row.BorrowerGeographicRegion,
  pCreditorType: row.pCreditorType,
  inferred_denominations_readable: row.inferred_denominations_readable,
}));
export const documentFlowRows: DocumentFlowRow[] = rawSankeyRows.map((row) => ({
  source: row.source,
  target: row.target,
  count: 1,
  DocumentYear: parseOptionalNumber(row.DocumentYear),
  complete_data: parseCompleteData(row.complete_data),
  CreditorGeographicRegion: row.CreditorGeographicRegion,
  BorrowerGeographicRegion: row.BorrowerGeographicRegion,
  pCreditorType: row.pCreditorType,
  inferred_denominations_readable: row.inferred_denominations_readable,
}));
export const creditorYearRows = commitCreditorYearJson.result[0]
  .data as YearlyRow[];
export const creditorTypeTotalRows = commitmentCreditorTypesJson.result[0]
  .data as CreditorTypeTotalRow[];
export const governingLawYearRows = commitmentGoverningLawYearJson.result[0]
  .data as YearlyRow[];
export const governingLawTotalRows = commitmentGoverningLawJson.result[0]
  .data as GoverningLawTotalRow[];
export const gtcYearRows = commitmentGtcYearJson.result[0].data as YearlyRow[];
export const gtcTotalRows = commitmentGtcJson.result[0].data as GtcTotalRow[];
export const denominationYearRows = commitDenominationYearJson.result[0]
  .data as YearlyRow[];
export const instrumentYearRows = commitInstrumentYearJson.result[0]
  .data as YearlyRow[];

const dfClausesJsonTyped = dfClausesJson as Record<
  string,
  Record<string, unknown>
>;

// Transform dfClausesJson from columnar format to row format
export const clauseRows: ClauseRow[] = Object.keys(
  dfClausesJsonTyped.DOCID || {},
).map((index) => {
  const row: ClauseRow = {};
  Object.keys(dfClausesJsonTyped).forEach((colName) => {
    row[colName] = dfClausesJsonTyped[colName]?.[index];
  });
  return row;
});

export const creditorTypeColumns =
  commitCreditorYearJson.result[0].colnames.filter(
    (column) => column !== "DocumentYear",
  );

export const denominationColumns =
  commitDenominationYearJson.result[0].colnames.filter(
    (column) => column !== "DocumentYear",
  );

export const instrumentColumns = commitInstrumentYearJson.result[0].colnames
  .filter((column) => column !== "DocumentYear")
  .filter((column) => column !== "<NULL>");

export const governingLawColumns =
  commitmentGoverningLawYearJson.result[0].colnames.filter(
    (column) => column !== "DocumentYear",
  );

export const gtcColumns = commitmentGtcYearJson.result[0].colnames.filter(
  (column) => column !== "DocumentYear",
);

export const allYears = Array.from(
  new Set(
    [
      ...creditorYearRows,
      ...denominationYearRows,
      ...instrumentYearRows,
      ...governingLawYearRows,
      ...gtcYearRows,
    ].map((row) => Math.round(Number(row.DocumentYear))),
  ),
).sort((a, b) => a - b);

// minYear is 2006 because the years prior have no data
export const minYear = 2006;
export const maxYear = allYears[allYears.length - 1] ?? 2025;
